import { ThemeProvider } from "@emotion/react";
import * as React from "react";

import { CheckCircle } from "@livechat/design-system-icons";
import { DesignToken, Icon, Link, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { inTrial } from "helpers/subscription";
import { calculatePrice } from "helpers/subscription-plans";
import { IRecurlyCoupon } from "store/entities/recurly";
import { ISubscription } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { useTaxChangePreview } from "../components/SubscriptionCardForm/tax-preview/use-tax-change-preview";
import { ContentBox, ContentBoxWrapper, SideNoteWrapper } from "../styled";

import { FirstPayment } from "./FirstPayment";
import { NextPayment } from "./NextPayment";

interface IProps {
  invoiceEmail: string;
  spareAgents: number;
  subscription: ISubscription | null;
  coupon: IRecurlyCoupon | null;
}

export const ThankYouPage: React.FC<IProps> = ({ subscription, coupon, ...props }) => {
  const { taxChangePreview } = useTaxChangePreview();
  const newPrice = subscription ? calculatePrice(subscription, coupon) : null;
  const newPriceDisplay = newPrice ? Math.round(newPrice * 100) / 100 : null;
  const nextInvoiceDate = subscription ? subscription.subscriptionExpiresAt : null;
  const trial = inTrial(subscription);
  const messageProps = {
    ...props,
    newPrice: newPriceDisplay,
    nextInvoiceDate,
    isTaxed: !!taxChangePreview.currentTax,
  };

  const handleG2Click = (): void => {
    trackEngagement("Subscription: G2 link clicked", EventPlace.Subscription, {
      "First payment": trial,
    });
  };

  return (
    <ThemeProvider theme={legacyTheme}>
      <ContentBoxWrapper withoutTitle>
        <ContentBox centered>
          <Flex justifyContent="center" marginBottom={withVar(SpacingToken.Spacing6)}>
            <Icon source={CheckCircle} size="xxxlarge" customColor={withVar(DesignToken.ActionPositiveDefault)} />
          </Flex>
          {trial && <FirstPayment {...messageProps} />}
          {!trial && <NextPayment {...messageProps} />}
          <SideNoteWrapper>
            <Text>
              We’re glad you’re enjoying HelpDesk.
              <br />
              If you have 5 minutes, we’d really appreciate your{" "}
              <Link
                href="https://www.g2.com/products/helpdesk/reviews"
                onClick={handleG2Click}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
              >
                review on G2
              </Link>
              .
            </Text>
          </SideNoteWrapper>
        </ContentBox>
      </ContentBoxWrapper>
    </ThemeProvider>
  );
};
